import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { fetchRegionOptions, fetchFuelTypeOptions } from "../../helpers";
import { fetchVehicleBrands, fetchVehicleModels, fetchVehiclesByCustomer, saveVehicle, deleteVehicle } from "../../models/shared/vehiclesModel";
import { isFamilyVehicle, publishVehicleUpdates, vehicleV1Code } from "../../models/shared/customerVehiclesV1Model";
import { legacyVehicleDraft, buildLegacyVehiclePayload } from "../../models/customer/customerVehiclesModel";
import { duplicateVinVehicle, DUPLICATE_VIN_MESSAGE } from "../../models/customer/customerProfileListModel";

export function useCustomerVehicles({ customer, open, setActiveSection, queriesList, loadQueries, onFieldChanged, message }) {
  const { authState } = useContext(AuthContext);
  const { can } = useAccess();
  const canManageVehicles = can("customers.manage_vehicles");
  const [vehicleSubForm, setVehicleSubForm] = useState(null);
  const [familyVehicleEditor, setFamilyVehicleEditor] = useState(null);
  useEffect(() => { setFamilyVehicleEditor(null); }, [open, customer?.customer_id]);
  const [vehicles, setVehicles] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, writeModelOptions] = useState([]);
  const modelRequest = useRef(0);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  // Resets, saves and the view's cancel action invalidate outstanding loads.
  const setModelOptions = useCallback(options => {
    modelRequest.current += 1;
    writeModelOptions(options);
    setLoadingModels(false);
  }, []);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesSaving, setVehiclesSaving] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);

  const [regionOptions, setRegionOptions] = useState([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
  useEffect(() => {
    if (!open) return;
    if (regionOptions.length === 0) fetchRegionOptions().then(setRegionOptions);
    if (fuelTypeOptions.length === 0) fetchFuelTypeOptions().then(setFuelTypeOptions);
  }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open || !customer) return;
    setVehicleSubForm(null);
    setEditingVehicleId(null);
    setModelOptions([]);
    loadVehicles(customer?.customer_id);
  }, [open, customer]); // eslint-disable-line
  useEffect(() => {
    if (!open || brandOptions.length > 0) return;
    setLoadingBrands(true);
    fetchVehicleBrands().then(setBrandOptions).finally(() => setLoadingBrands(false));
  }, [open]); // eslint-disable-line


  async function loadVehicleModels(brandId) {
    if (!brandId) { setModelOptions([]); return; }
    const token = ++modelRequest.current;
    setLoadingModels(true);
    try {
      const options = await fetchVehicleModels(brandId);
      if (token === modelRequest.current) writeModelOptions(options);
    } catch { if (token === modelRequest.current) writeModelOptions([]); }
    finally { if (token === modelRequest.current) setLoadingModels(false); }
  }

  async function loadVehicles(customerId) {
    if (!customerId) { setVehicles([]); return; }
    setVehiclesLoading(true);
    try {
      const list = await fetchVehiclesByCustomer(customerId);
      setVehicles(list);
      return list;
    } catch { setVehicles([]); }
    finally { setVehiclesLoading(false); }
  }


  const openAddVehicleForm = () => {
    if (!canManageVehicles) return;
    setActiveSection("vehicles");
    setVehicleSubForm(null);
    setEditingVehicleId(null);
    setFamilyVehicleEditor({});
  };
  const routeLegacyVehicle = useCallback(vehicle => {
    setFamilyVehicleEditor(null);
    setActiveSection("vehicles");
    setEditingVehicleId(vehicle.id);
    setVehicleSubForm(legacyVehicleDraft(vehicle));
    if (vehicle.brand) loadVehicleModels(vehicle.brand);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps




  const saveVehicleForm = async () => {
    if (vehiclesLoading) { message.warning("Please wait for the existing vehicles to load."); return; }
    if (duplicateVinVehicle(vehicles, vehicleSubForm.vin, editingVehicleId)) { message.warning(DUPLICATE_VIN_MESSAGE); return; }
    if (!vehicleSubForm.brand_id) { message.warning("Please select a brand"); return; }
    if (!vehicleSubForm.model_id) { message.warning("Please select a model"); return; }
    setVehiclesSaving(true);
    const isEdit = !!editingVehicleId;
    try {
      const payload = buildLegacyVehiclePayload(vehicleSubForm, editingVehicleId, customer.customer_id, authState.id);
      const res = await saveVehicle(payload, { isEdit });
      if (!res.success) throw new Error(res.message);
      message.success(isEdit ? "Vehicle updated" : "Vehicle added");
      setVehicleSubForm(null);
      setEditingVehicleId(null);
      setModelOptions([]);
      publishVehicleUpdates(customer.customer_id, await loadVehicles(customer.customer_id) || []);
    } catch (e) {
      if (vehicleV1Code(e) === "CANONICAL_VEHICLE") {
        setVehicleSubForm(null);
        setFamilyVehicleEditor({ id: editingVehicleId });
      }
      message.error(e.message || (isEdit ? "Failed to update vehicle" : "Failed to add vehicle"));
    } finally {
      setVehiclesSaving(false);
    }
  };

  const editVehicle = v => {
    if (isFamilyVehicle(v)) {
      setVehicleSubForm(null);
      setFamilyVehicleEditor({ id: v.id });
      return;
    }
    setActiveSection("vehicles");
    setEditingVehicleId(v.id);
    setVehicleSubForm(legacyVehicleDraft(v));
    if (v.brand) loadVehicleModels(v.brand);
    return true;
  };

  const handleDeleteVehicle = async v => {
    try {
      const res = await deleteVehicle(v.id);
      if (!res.success) throw new Error(res.message);
      setVehicles(prev => prev.filter(x => x.id !== v.id));
    } catch (e) {
      message.error(e.message || "Failed to delete vehicle");
    }
  };

  const onFamilySaved = async () => {
    const nextVehicles = await loadVehicles(customer.customer_id) || [];
    publishVehicleUpdates(customer.customer_id, nextVehicles);
    if (queriesList.length) await loadQueries();
    onFieldChanged?.({ customer_id: customer.customer_id, customer_vehicles: nextVehicles });
  };

  return { vehicleSubForm, setVehicleSubForm, familyVehicleEditor, setFamilyVehicleEditor, vehicles, brandOptions, modelOptions, setModelOptions,
    loadingBrands, loadingModels, vehiclesLoading, vehiclesSaving, editingVehicleId, setEditingVehicleId, regionOptions, fuelTypeOptions,
    loadVehicleModels, loadVehicles, openAddVehicleForm, routeLegacyVehicle, saveVehicleForm, editVehicle, handleDeleteVehicle, onFamilySaved };
}
